import React, { useState, useEffect, useCallback } from 'react';
import { confirmar } from '../components/confirmacao';
import apiService from '../services/apiService';

// Página admin: lista os endpoints publicados (via MCP/Claude) e gerencia tokens.
// Consome o BFF /api/endpoints* que faz proxy para apidash.alia.do/admin.

const EXPIRY_OPTIONS = [
  { label: '24 horas', minutes: 60 * 24 },
  { label: '7 dias', minutes: 60 * 24 * 7 },
  { label: '30 dias', minutes: 60 * 24 * 30 },
  { label: '90 dias', minutes: 60 * 24 * 90 },
  { label: 'Sem expiração', minutes: '' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch { return iso; }
}

export default function EndpointsPublicados() {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);       // { ...endpoint, tokens: [] }
  const [detailLoading, setDetailLoading] = useState(false);
  const [expiry, setExpiry] = useState(EXPIRY_OPTIONS[1].minutes);
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState(null);       // token recém gerado
  const [copied, setCopied] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setEndpoints(await apiService.getEndpoints());
    } catch (e) {
      setError('Erro ao carregar endpoints. Confira DASHAPI_URL/DASHAPI_SERVICE_TOKEN no servidor.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (ep) => {
    if (openId === ep.id) { setOpenId(null); setDetail(null); setIssued(null); return; }
    setOpenId(ep.id); setIssued(null); setDetail(null); setDetailLoading(true);
    try { setDetail(await apiService.getEndpoint(ep.id)); }
    catch { setDetail({ ...ep, tokens: [] }); }
    finally { setDetailLoading(false); }
  };

  const copy = async (text, field) => {
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopied(field); setTimeout(() => setCopied(null), 2000);
    } catch { /* noop */ }
  };

  const genToken = async (ep) => {
    setBusy(true); setIssued(null);
    try {
      const body = { label: label || undefined };
      if (expiry !== '') body.expires_in_minutes = Number(expiry);
      const res = await apiService.generateEndpointToken(ep.id, body);
      setIssued(res);
      setDetail(await apiService.getEndpoint(ep.id));
      setLabel('');
    } catch { setError('Erro ao gerar token.'); }
    finally { setBusy(false); }
  };

  const revoke = async (ep, jti) => {
    if (!(await confirmar('Revogar este token? Chamadas com ele passam a retornar 401 imediatamente.', { titulo: 'Revogar token', perigo: true, rotuloConfirmar: 'Revogar' }))) return;
    setBusy(true);
    try { await apiService.revokeEndpointToken(ep.id, jti); setDetail(await apiService.getEndpoint(ep.id)); }
    catch { setError('Erro ao revogar token.'); }
    finally { setBusy(false); }
  };

  const del = async (ep) => {
    if (!(await confirmar(`Apagar o endpoint "${ep.slug}"? Ele some da lista e os tokens dele param de funcionar.`, { titulo: 'Apagar endpoint', perigo: true, rotuloConfirmar: 'Apagar' }))) return;
    setBusy(true); setError(null);
    try {
      await apiService.deleteEndpoint(ep.id);
      if (openId === ep.id) { setOpenId(null); setDetail(null); setIssued(null); }
      await load();
    } catch { setError('Erro ao apagar endpoint.'); }
    finally { setBusy(false); }
  };

  // Gera um valor de exemplo por parâmetro (usa default se houver; senão pelo tipo/nome).
  const exampleValue = (name, spec) => {
    const s = spec || {};
    if (s.default !== undefined && s.default !== null && s.default !== '') return s.default;
    const t = (s.type || 'string').toLowerCase();
    const n = (name || '').toLowerCase();
    const d = new Date();
    const ymd = d.toISOString().slice(0, 10);   // 2026-07-27
    const ym = d.toISOString().slice(0, 7);     // 2026-07
    const dt = d.toISOString().slice(0, 19) + 'Z';
    if (t === 'date') return /mes|month/.test(n) ? ym : ymd;
    if (t === 'datetime' || t === 'timestamp') return dt;
    if (t === 'bool' || t === 'boolean') return true;
    if (t === 'int' || t === 'integer') return /(^id$|_id$|id$)/.test(n) ? 1 : 10;
    if (['float', 'number', 'numeric', 'decimal'].includes(t)) return 9.99;
    // string: heurística por nome para datas/mês
    if (/mes|month/.test(n)) return ym;
    if (/data|date|dia|in[ií]cio|inicio|fim|start|end|per[ií]odo|periodo/.test(n)) return ymd;
    return 'exemplo';
  };

  const buildCurl = (ep, token) => {
    const params = detail?.params_schema || {};
    const method = String(detail?.http_method || ep.http_method || 'GET').toUpperCase();
    const auth = `-H "Authorization: Bearer ${token}"`;
    const base = `https://apidash.alia.do/q/${ep.slug}`;
    if (method === 'POST') {
      const body = {};
      Object.entries(params).forEach(([k, spec]) => { body[k] = exampleValue(k, spec); });
      return `curl -X POST ${auth} -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(body)}' "${base}"`;
    }
    const qs = Object.keys(params).length
      ? '?' + Object.entries(params).map(([k, spec]) => `${k}=${encodeURIComponent(exampleValue(k, spec))}`).join('&')
      : '';
    return `curl ${auth} "${base}${qs}"`;
  };

  const filtered = endpoints.filter(e => {
    const q = search.toLowerCase();
    return !q || [e.slug, e.title, e.description, e.author_name, e.author_email]
      .filter(Boolean).some(v => String(v).toLowerCase().includes(q));
  });

  const S = {
    wrap: { maxWidth: 1200, margin: '0 auto', padding: '1.5rem' },
    input: { padding: '8px 12px', borderRadius: 8, border: '1px solid #ccc', width: 320, maxWidth: '100%' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: 16 },
    th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #eee', fontSize: 13, color: '#555' },
    td: { padding: '10px 12px', borderBottom: '1px solid #f0f0f0', fontSize: 14, verticalAlign: 'top' },
    btn: { padding: '6px 12px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 },
    btnP: { padding: '6px 12px', borderRadius: 8, border: 'none', background: '#4f46e5', color: '#fff', cursor: 'pointer', fontSize: 13 },
    panel: { background: '#fafafe', border: '1px solid #eee', borderRadius: 10, padding: 16 },
    code: { fontFamily: 'monospace', fontSize: 12, background: '#f4f4f8', padding: '6px 8px', borderRadius: 6, wordBreak: 'break-all' },
    tag: { fontSize: 11, padding: '2px 8px', borderRadius: 999, background: '#eef', color: '#334' },
  };

  return (
    <div style={S.wrap}>
      <h1 style={{ fontSize: 22, marginBottom: 4 }}>🔌 Endpoints Publicados</h1>
      <p style={{ color: '#666', marginTop: 0 }}>
        Endpoints criados via Claude (Dash Builder). Gere um token para consumir cada um em
        {' '}<code style={{ fontFamily: 'monospace' }}>apidash.alia.do/q/&lt;slug&gt;</code>.
      </p>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 12 }}>
        <input style={S.input} placeholder="Buscar por slug, título, autor…" value={search} onChange={e => setSearch(e.target.value)} />
        <button style={S.btn} onClick={load} disabled={loading}>↻ Atualizar</button>
        <span style={{ color: '#888', fontSize: 13 }}>{filtered.length} endpoint(s)</span>
      </div>

      {error && <div style={{ color: '#b00', marginTop: 12 }}>{error}</div>}
      {loading ? <p style={{ marginTop: 24 }}>Carregando…</p> : (
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Slug</th>
              <th style={S.th}>Descrição</th>
              <th style={S.th}>Autor</th>
              <th style={S.th}>Criado em</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td style={S.td} colSpan={5}>Nenhum endpoint publicado ainda. Publique um via Claude (tool <code>publish_endpoint</code>).</td></tr>
            )}
            {filtered.map(ep => (
              <React.Fragment key={ep.id}>
                <tr>
                  <td style={S.td}>
                    <strong>{ep.slug}</strong>{!ep.is_active && <span style={{ ...S.tag, background: '#fee', color: '#900', marginLeft: 8 }}>inativo</span>}
                    <div style={{ color: '#999', fontSize: 12 }}>{ep.title}</div>
                  </td>
                  <td style={S.td}>{ep.description || '—'}</td>
                  <td style={S.td}>{ep.author_name || '—'}<div style={{ color: '#999', fontSize: 12 }}>{ep.author_email}</div></td>
                  <td style={S.td}>{fmtDate(ep.created_at)}</td>
                  <td style={S.td}>
                    <button style={S.btn} onClick={() => openDetail(ep)}>{openId === ep.id ? 'Fechar' : 'Gerenciar'}</button>
                    <button style={{ ...S.btn, marginLeft: 6, color: '#b00', borderColor: '#f3c4c4' }} onClick={() => del(ep)} disabled={busy}>Apagar</button>
                  </td>
                </tr>
                {openId === ep.id && (
                  <tr>
                    <td style={{ ...S.td, background: '#fafafe' }} colSpan={5}>
                      {detailLoading ? 'Carregando…' : (
                        <div style={S.panel}>
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontSize: 13, color: '#555', marginBottom: 4 }}>URL</div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <span style={S.code}>{`https://apidash.alia.do/q/${ep.slug}`}</span>
                              <button style={S.btn} onClick={() => copy(`https://apidash.alia.do/q/${ep.slug}`, 'url-' + ep.id)}>
                                {copied === 'url-' + ep.id ? '✅ Copiado' : '📋 Copiar URL'}
                              </button>
                            </div>
                          </div>

                          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0' }}>
                            <span style={{ fontSize: 13, color: '#555' }}>Gerar token:</span>
                            <select style={S.input} value={expiry} onChange={e => setExpiry(e.target.value)}>
                              {EXPIRY_OPTIONS.map(o => <option key={o.label} value={o.minutes}>{o.label}</option>)}
                            </select>
                            <input style={{ ...S.input, width: 180 }} placeholder="rótulo (opcional)" value={label} onChange={e => setLabel(e.target.value)} />
                            <button style={S.btnP} onClick={() => genToken(ep)} disabled={busy}>Gerar token</button>
                          </div>

                          {issued && (
                            <div style={{ background: '#eefbf1', border: '1px solid #b7ebc6', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                              <div style={{ fontSize: 13, marginBottom: 6 }}>Token gerado — copie agora (não é exibido de novo):</div>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <span style={S.code}>{issued.token}</span>
                                <button style={S.btn} onClick={() => copy(issued.token, 'tok-' + ep.id)}>
                                  {copied === 'tok-' + ep.id ? '✅ Copiado' : '📋 Copiar Token'}
                                </button>
                              </div>
                              <div style={{ color: '#666', fontSize: 12, marginTop: 6 }}>
                                Expira: {issued.expires_at ? fmtDate(issued.expires_at) : 'sem expiração'}
                              </div>
                              <div style={{ marginTop: 10 }}>
                                <div style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>curl de exemplo (copie e rode):</div>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                  <span style={{ ...S.code, whiteSpace: 'pre-wrap' }}>{buildCurl(ep, issued.token)}</span>
                                  <button style={S.btn} onClick={() => copy(buildCurl(ep, issued.token), 'curl-' + ep.id)}>
                                    {copied === 'curl-' + ep.id ? '✅ Copiado' : '📋 Copiar curl'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}

                          <div style={{ fontSize: 13, color: '#555', marginBottom: 6 }}>Tokens ({(detail?.tokens || []).length})</div>
                          <table style={{ ...S.table, marginTop: 0 }}>
                            <thead><tr>
                              <th style={S.th}>Rótulo</th><th style={S.th}>Expira</th><th style={S.th}>Status</th><th style={S.th}>Criado</th><th style={S.th}></th>
                            </tr></thead>
                            <tbody>
                              {(detail?.tokens || []).length === 0 && <tr><td style={S.td} colSpan={5}>Nenhum token.</td></tr>}
                              {(detail?.tokens || []).map(t => (
                                <tr key={t.jti}>
                                  <td style={S.td}>{t.label || '—'}</td>
                                  <td style={S.td}>{t.expires_at ? fmtDate(t.expires_at) : 'sem expiração'}</td>
                                  <td style={S.td}>{t.revoked_at ? <span style={{ ...S.tag, background: '#fee', color: '#900' }}>revogado</span> : <span style={{ ...S.tag, background: '#eafbea', color: '#161' }}>ativo</span>}</td>
                                  <td style={S.td}>{fmtDate(t.created_at)}</td>
                                  <td style={S.td}>{!t.revoked_at && <button style={S.btn} onClick={() => revoke(ep, t.jti)} disabled={busy}>Revogar</button>}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
